/**
 * Public API client for candidate OTC session endpoints. No auth header — the
 * OTC in the URL is the bearer. Seat token (MCQ/Tech) is sent via cookie when
 * credentials are available, plus X-HireIQ-Assessment-Seat / Writer headers.
 */
import axios from 'axios';
import { API_V1 } from './api';

const SEAT_STORAGE_KEY = 'hireiq_assess_seat';
const WRITER_STORAGE_KEY = 'hireiq_assess_writer';

export const otcClient = axios.create({ baseURL: API_V1, withCredentials: true });

function getOrCreateWriterId() {
  if (typeof window === 'undefined') return null;
  try {
    let id = sessionStorage.getItem(WRITER_STORAGE_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `w-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(WRITER_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function storeAssessmentSeat(token) {
  if (!token || typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SEAT_STORAGE_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearAssessmentSeat() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(SEAT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getAssessmentSeat() {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(SEAT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Attach SEB JS API keys + assessment seat/writer when present. */
otcClient.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const sebKeys = window.SafeExamBrowser?.security;
  if (sebKeys?.configKey) {
    config.headers['X-HireIQ-SEB-ConfigKey-JS'] = sebKeys.configKey;
  }
  if (sebKeys?.browserExamKey) {
    config.headers['X-HireIQ-SEB-BrowserExamKey-JS'] = sebKeys.browserExamKey;
  }
  const seat = getAssessmentSeat();
  if (seat) {
    config.headers['X-HireIQ-Assessment-Seat'] = seat;
  }
  const writer = getOrCreateWriterId();
  if (writer) {
    config.headers['X-HireIQ-Assessment-Writer'] = writer;
  }
  return config;
});

/** Long-running video uploads need a generous timeout (default axios is none). */
const VB_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
const VB_API_TIMEOUT_MS = 60 * 1000;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const isClientError = (err) => {
  const status = err?.response?.status;
  return Boolean(status && status >= 400 && status < 500 && status !== 429);
};

export const otcVerify = async (otc) =>
  (await otcClient.get(`/candidate-session/${otc}`, { timeout: VB_API_TIMEOUT_MS })).data;

export const otcStart = async (otc) => {
  const data = (await otcClient.post(`/candidate-session/${otc}/start`, {}, { timeout: VB_API_TIMEOUT_MS })).data;
  if (data?.seat_token) storeAssessmentSeat(data.seat_token);
  return data;
};

export const otcSaveAnswers = async (otc, answers) =>
  (await otcClient.put(
    `/candidate-session/${otc}/answers`,
    { answers },
    { timeout: VB_API_TIMEOUT_MS },
  )).data;

export const otcHeartbeat = async (otc) =>
  (await otcClient.post(`/candidate-session/${otc}/heartbeat`, {}, { timeout: VB_API_TIMEOUT_MS })).data;

export const otcAbandon = async (otc) => {
  const data = (await otcClient.post(`/candidate-session/${otc}/abandon`, {}, { timeout: VB_API_TIMEOUT_MS })).data;
  clearAssessmentSeat();
  return data;
};

export const otcSebVerify = async (otc, { page_url, js_config_key, js_browser_exam_key } = {}) =>
  (await otcClient.post(
    '/seb/verify',
    {
      otc,
      page_url: page_url || (typeof window !== 'undefined' ? window.location.href.split('#')[0] : ''),
      js_config_key,
      js_browser_exam_key,
    },
    { timeout: VB_API_TIMEOUT_MS },
  )).data;

export const otcUploadInit = async (otc, questionId, body) =>
  (await otcClient.post(
    `/candidate-session/${otc}/responses/${questionId}/upload-init`,
    body,
    { timeout: VB_API_TIMEOUT_MS },
  )).data;

export const otcFinalize = async (otc, questionId, body) =>
  (await otcClient.post(
    `/candidate-session/${otc}/responses/${questionId}/finalize`,
    body,
    { timeout: VB_API_TIMEOUT_MS },
  )).data;

export const otcSubmit = async (otc) =>
  (await otcClient.post(`/candidate-session/${otc}/submit`, {}, { timeout: VB_API_TIMEOUT_MS })).data;

// ---- Panellist feedback form (public, token-gated) ----
export const panelFeedbackGet = async (token) =>
  (await otcClient.get(`/panel-feedback/${token}`, { timeout: VB_API_TIMEOUT_MS })).data;

export const panelFeedbackSubmit = async (token, body) =>
  (await otcClient.post(`/panel-feedback/${token}/submit`, body, { timeout: VB_API_TIMEOUT_MS })).data;

// ---- Panellist briefing resume (public, token-gated) ----
export const panelResumeGet = async (token) =>
  (await otcClient.get(`/panel-resume/${token}`, { timeout: VB_API_TIMEOUT_MS })).data;

/**
 * Poll the verify endpoint until a finalized response appears (transport-failure recovery).
 */
export async function recoverFinalizedResponse(otc, questionId, { maxAttempts = 4 } = {}) {
  for (let i = 0; i < maxAttempts; i += 1) {
    try {
      const data = await otcVerify(otc);
      const row = (data?.responses || []).find((r) => r.question_id === questionId);
      if (row?.status === 'finalized') return row;
    } catch {
      /* retry */
    }
    if (i < maxAttempts - 1) await sleep(1500 * (i + 1));
  }
  return null;
}

/** Upload a Blob to S3 via presigned PUT with upload progress (XHR). */
export function uploadBlobToPresignedWithProgress(url, blob, contentType, onProgress, timeoutMs = VB_UPLOAD_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = timeoutMs;
    if (onProgress) {
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(Object.assign(new Error(`S3 upload failed: ${xhr.status}`), { status: xhr.status }));
    };
    xhr.onerror = () => reject(Object.assign(new Error('S3 upload network error'), { isNetwork: true }));
    xhr.ontimeout = () => reject(Object.assign(new Error('S3 upload timed out'), { isNetwork: true }));
    xhr.send(blob);
  });
}

/** @deprecated Prefer uploadBlobToPresignedWithProgress */
export async function uploadBlobToPresigned(url, blob, contentType) {
  await uploadBlobToPresignedWithProgress(url, blob, contentType);
}

/**
 * Direct browser → S3 upload (presigned PUT) with small API init/finalize calls.
 * Avoids routing large video blobs through the backend proxy (Sprint 11.7 workaround).
 */
export async function otcDirectUpload(
  otc,
  questionId,
  blob,
  contentType,
  durationSec,
  onProgress,
  { intent = 'upload', onSaving } = {},
) {
  const initBody = { content_type: contentType, duration_sec: durationSec, intent };

  let initOut = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      initOut = await otcUploadInit(otc, questionId, initBody);
      break;
    } catch (e) {
      if (isClientError(e) || attempt === 2) throw e;
      await sleep(1500 * (attempt + 1));
    }
  }

  if (initOut.already_finalized) {
    return {
      id: initOut.response_id,
      question_id: questionId,
      status: 'finalized',
      retakes_used: initOut.retakes_used,
    };
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await uploadBlobToPresignedWithProgress(
        initOut.upload_url,
        blob,
        contentType,
        (pct) => onProgress?.(Math.max(10, Math.min(90, pct))),
      );
      break;
    } catch (e) {
      if (attempt === 2) throw e;
      await sleep(2000 * (attempt + 1));
    }
  }

  onSaving?.();
  onProgress?.(92);

  const finalizeBody = {
    s3_object_id: initOut.s3_object_id,
    s3_key: initOut.s3_key,
    duration_sec: durationSec,
  };

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const out = await otcFinalize(otc, questionId, finalizeBody);
      onProgress?.(100);
      return out;
    } catch (e) {
      if (e?.response?.status === 409 || e?.response?.status === 410) throw e;
      const recovered = await recoverFinalizedResponse(otc, questionId, { maxAttempts: 2 });
      if (recovered) {
        onProgress?.(100);
        return recovered;
      }
      if (isClientError(e) || attempt === 3) throw e;
      await sleep(2000 * (attempt + 1));
    }
  }

  throw new Error('Upload failed');
}

/**
 * Backend-proxied upload fallback (large blob through API). Kept for environments
 * where S3 bucket CORS cannot be configured; prefer otcDirectUpload.
 */
export const otcProxyUpload = async (
  otc,
  questionId,
  blob,
  contentType,
  durationSec,
  onProgress,
  { intent = 'upload', onSaving } = {},
) => {
  const form = new FormData();
  const ext = contentType.includes('mp4') ? 'mp4' : (contentType.includes('matroska') ? 'mkv' : 'webm');
  form.append('file', blob, `response.${ext}`);
  form.append('duration_sec', String(durationSec));
  form.append('intent', intent);
  const res = await otcClient.post(
    `/candidate-session/${otc}/responses/${questionId}/upload`,
    form,
    {
      timeout: VB_UPLOAD_TIMEOUT_MS,
      onUploadProgress: (evt) => {
        if (!onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onProgress(pct);
        if (pct >= 100 && onSaving) onSaving();
      },
    },
  );
  return res.data;
};

/** MCQ / Tech continuous session recording — upload-init / finalize (SEB headers via otcClient). */
export const otcSessionRecordingUploadInit = async (otc, body) =>
  (await otcClient.post(
    `/candidate-session/${otc}/session-recording/upload-init`,
    body,
    { timeout: VB_API_TIMEOUT_MS },
  )).data;

export const otcSessionRecordingFinalize = async (otc, body) =>
  (await otcClient.post(
    `/candidate-session/${otc}/session-recording/finalize`,
    body,
    { timeout: VB_API_TIMEOUT_MS },
  )).data;

/**
 * Direct browser → S3 session recording upload. Fail-soft callers should catch.
 * @param {number} [timeoutMs] — abandon paths may pass a shorter timeout.
 */
export async function otcSessionRecordingUpload(
  otc,
  blob,
  contentType,
  durationSec,
  { onProgress, onSaving, timeoutMs = VB_UPLOAD_TIMEOUT_MS } = {},
) {
  let initOut = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      initOut = await otcSessionRecordingUploadInit(otc, {
        content_type: contentType,
        duration_sec: durationSec,
      });
      break;
    } catch (e) {
      if (isClientError(e) || attempt === 2) throw e;
      await sleep(1500 * (attempt + 1));
    }
  }

  if (initOut.already_finalized) {
    return { assessment_id: initOut.assessment_id, recording_status: 'available' };
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await uploadBlobToPresignedWithProgress(
        initOut.upload_url,
        blob,
        contentType,
        (pct) => onProgress?.(Math.max(10, Math.min(90, pct))),
        timeoutMs,
      );
      break;
    } catch (e) {
      if (attempt === 2) throw e;
      await sleep(2000 * (attempt + 1));
    }
  }

  onSaving?.();
  onProgress?.(92);

  const finalizeBody = {
    s3_object_id: initOut.s3_object_id,
    s3_key: initOut.s3_key,
    duration_sec: durationSec,
  };

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const out = await otcSessionRecordingFinalize(otc, finalizeBody);
      onProgress?.(100);
      return out;
    } catch (e) {
      if (e?.response?.status === 409 || e?.response?.status === 410) throw e;
      if (isClientError(e) || attempt === 3) throw e;
      await sleep(2000 * (attempt + 1));
    }
  }

  throw new Error('Session recording upload failed');
}

/** Parse server expires_at + optional server_now for timer skew correction. */
export function resolveExpiresAtMs(rawExp, serverNow, fallbackTotalSeconds = 0) {
  if (!rawExp) {
    return Date.now() + (fallbackTotalSeconds * 1000);
  }
  const isoUtc = /[Zz]|[+-]\d{2}:?\d{2}$/.test(rawExp) ? rawExp : (`${rawExp}Z`);
  const expiresAt = new Date(isoUtc).getTime();
  if (serverNow) {
    const sn = /[Zz]|[+-]\d{2}:?\d{2}$/.test(serverNow) ? serverNow : (`${serverNow}Z`);
    const skew = Date.now() - new Date(sn).getTime();
    return expiresAt + skew;
  }
  return expiresAt;
}
